/**
 * The user-tunable half of the theme: an accent hue and two appearance presets.
 *
 * `tokens.css` ships the design and the 50 themes around it — five surface
 * presets by five contrast presets, per mode, all generated. This file only
 * names the choices and writes them to the document. Every color still
 * resolves in CSS, so a mode flip re-resolves on its own and there is nothing
 * here to keep in sync.
 *
 * The presets are DISCRETE on purpose. Legibility is a relationship between a
 * fill and the thing drawn on it, and no expression CSS can evaluate preserves
 * it — there is no contrast function in `calc()`. So the safe values are
 * solved ahead of time and the control picks between them. A continuous slider
 * could only be made safe by clamping it, and a clamped slider is one whose
 * travel stops doing anything before it stops moving.
 */

/**
 * The pickable accents. Every name has `[data-accent]` blocks in `tokens.css`
 * for both modes and all five surface presets.
 *
 * This is deliberately NOT the entity palette, and the two must not be merged
 * back together. A palette name is persisted on rows and is permanent — it can
 * never be removed once shipped. An accent is a product decision about how
 * many themes to offer, and shortening the list has to stay cheap. They also
 * answer different questions: the palette needs seventeen hues far enough
 * apart to tell one team from another at a glance, while an accent only has to
 * look good as the single brand color of an entire interface.
 */
export const ACCENT_COLORS = [
  "emerald",
  "indigo",
  "amber",
  "violet",
  "red",
  "cyan",
] as const;

export type AccentColor = (typeof ACCENT_COLORS)[number];

const ACCENT_SET: ReadonlySet<string> = new Set(ACCENT_COLORS);

/**
 * Whether a string names a pickable accent. Stored preferences arrive as
 * free-form strings, so this is the only sanctioned narrowing — never `as`.
 */
export function isAccentColor(
  value: string | null | undefined,
): value is AccentColor {
  return value != null && ACCENT_SET.has(value);
}

/**
 * How light the surfaces sit, dimmest to brightest, in both modes.
 *
 * The travel is not symmetric between the modes and cannot be: light mode's
 * page starts a hair off white with the panels already at white, so nearly all
 * of its range is downward, while dark mode moves freely both ways. What the
 * two share is that the ramp keeps its proportions at every stop. The
 * hairlines are solved against whatever the fills settle at, so as the fills
 * converge the hairlines take over the separation the fills gave up.
 */
export const SURFACE_PRESETS = [
  "dimmest",
  "dim",
  "default",
  "bright",
  "brightest",
] as const;

export type SurfacePreset = (typeof SURFACE_PRESETS)[number];

/**
 * How hard text reads against whatever the surfaces settled on.
 *
 * These are contrast TARGETS, not lightness values, and that is the whole
 * safety property: no combination of the two controls can produce something
 * unreadable, because the text is solved against the surface rather than moved
 * independently of it.
 *
 * The middle three names are the CSS `prefers-contrast` keywords, so the
 * control can seed itself from the operating system's own setting.
 */
export const CONTRAST_PRESETS = [
  "least",
  "less",
  "default",
  "more",
  "most",
] as const;

export type ContrastPreset = (typeof CONTRAST_PRESETS)[number];

const SURFACE_SET: ReadonlySet<string> = new Set(SURFACE_PRESETS);
const CONTRAST_SET: ReadonlySet<string> = new Set(CONTRAST_PRESETS);

/** Whether a string names a surface preset. The only sanctioned narrowing. */
export function isSurfacePreset(
  value: string | null | undefined,
): value is SurfacePreset {
  return value != null && SURFACE_SET.has(value);
}

/** Whether a string names a contrast preset. The only sanctioned narrowing. */
export function isContrastPreset(
  value: string | null | undefined,
): value is ContrastPreset {
  return value != null && CONTRAST_SET.has(value);
}

/** The shipped design: no accent, both axes at their middle stop. */
export const DEFAULT_SURFACE_PRESET: SurfacePreset = "default";
export const DEFAULT_CONTRAST_PRESET: ContrastPreset = "default";

export interface ThemeAppearance {
  /** `null` keeps the shipped near-black primary. */
  accent: AccentColor | null;
  surface: SurfacePreset;
  contrast: ContrastPreset;
}

export const DEFAULT_THEME_APPEARANCE: ThemeAppearance = {
  accent: null,
  surface: DEFAULT_SURFACE_PRESET,
  contrast: DEFAULT_CONTRAST_PRESET,
};

/**
 * Coerces a stored surface preset, falling back to the shipped default.
 *
 * Preferences travel untyped — absent while unloaded, arbitrary if a row was
 * ever hand-edited — and an unrecognised one has to land on something legible
 * rather than on an attribute value that matches no block at all.
 */
export function resolveSurfacePreset(
  value: string | null | undefined,
): SurfacePreset {
  return isSurfacePreset(value) ? value : DEFAULT_SURFACE_PRESET;
}

/** Coerces a stored contrast preset. See {@link resolveSurfacePreset}. */
export function resolveContrastPreset(
  value: string | null | undefined,
): ContrastPreset {
  return isContrastPreset(value) ? value : DEFAULT_CONTRAST_PRESET;
}


/**
 * Writes the appearance to `<html>`.
 *
 * That element is the single write target on purpose: `:root` and `.dark` are
 * both selectors on it, so the preset attributes compose with either without a
 * "which mode am I in" branch, and `data-accent` sits on the same element the
 * accent blocks are written against.
 *
 * A default REMOVES its attribute rather than writing the name, so an
 * untouched app leaves no trace on the document and the authored cascade is
 * what a reader sees in devtools. The generator omits that cell for the same
 * reason — `:root` and `.dark` already say it.
 */
export function applyThemeAppearance(appearance: ThemeAppearance): void {
  const root = document.documentElement;

  setOrRemove(root, "data-accent", appearance.accent);
  setOrRemove(
    root,
    "data-surface",
    appearance.surface === DEFAULT_SURFACE_PRESET ? null : appearance.surface,
  );
  setOrRemove(
    root,
    "data-contrast",
    appearance.contrast === DEFAULT_CONTRAST_PRESET
      ? null
      : appearance.contrast,
  );
}

function setOrRemove(
  root: HTMLElement,
  attribute: string,
  value: string | null,
): void {
  if (value === null) root.removeAttribute(attribute);
  else root.setAttribute(attribute, value);
}
